"use client"

import { useState, useContext, useEffect } from "react"
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  InputAdornment,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import BusinessIcon from "@mui/icons-material/Business"
import PersonIcon from "@mui/icons-material/Person"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import BadgeIcon from "@mui/icons-material/Badge"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import HomeIcon from "@mui/icons-material/Home"
import ApartmentIcon from "@mui/icons-material/Apartment"
import { ErpContext } from "../../context/ErpContext.js"

const ModalCrearActualizarEntidad = ({ open, handleClose, entidad, idTipoEntidad }) => {
  const theme = useTheme()
  const { postEntidad, updateEntidad, condicionIva, getCondicionIva, getCondicionIvaByEntidad, isNavbarCollapsed,
  } = useContext(ErpContext)

  // Estados
  const [entidadData, setEntidadData] = useState(entidad)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [errors, setErrors] = useState({})
  const [idCondicionIvaEntidad, setIdCondicionIvaEntidad] = useState("")

  // Inicializar datos cuando se abre el modal
  useEffect(() => {
    if (open) {
      setEntidadData(entidad)
      setErrors({})
      setError(null)
      
      // Cargar condicionIva si no están cargadas
      if (condicionIva.length === 0) {
        getCondicionIva()
      }

      // Si es edición, cargar la condición IVA actual de la entidad
      if (entidad.id) {
        loadCondicionIvaForEntidad(entidad.id)
      } else {
        setIdCondicionIvaEntidad("")
      }
    }
  }, [open, entidad])

  const loadCondicionIvaForEntidad = async (idEntidad) => {
    try {
      const condicionIvaData = await getCondicionIvaByEntidad(idEntidad)
      if (condicionIvaData && condicionIvaData.idCondicionIva) {
        setIdCondicionIvaEntidad(condicionIvaData.idCondicionIva)
      } else {
        setIdCondicionIvaEntidad("")
      }
    } catch (error) {
      console.error("Error al cargar condición IVA:", error)
      setIdCondicionIvaEntidad("")
    }
  }

  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target
    setEntidadData({ ...entidadData, [name]: value })

    // Limpiar error del campo cuando se modifica
    if (errors[name]) {
      setErrors({ ...errors, [name]: null })
    }
  }

  // Validar formulario
  const validateForm = () => {
    const newErrors = {}

    // Validar campos requeridos
    if (!entidadData.descripcion?.trim()) {
      newErrors.descripcion = "El nombre es requerido"
    }

    if (entidadData.email && !/\S+@\S+\.\S+/.test(entidadData.email)) {
      newErrors.email = "Email inválido"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Guardar cambios
  const handleSave = async () => {
    if (!validateForm()) return

    setLoading(true)
    setError(null)

    try {
      if (entidadData.id) {
        // Si la entidad ya tiene ID -> ACTUALIZAR
        await updateEntidad(
          entidadData.id,
          entidadData.descripcion,
          entidadData.apellido,
          entidadData.telefono,
          entidadData.email,
          entidadData.dniCuitCuil,
          entidadData.direccion,
          entidadData.localidad,
          entidadData.provincia,
          idTipoEntidad,
          idCondicionIvaEntidad,
        )
      } else {
        // Si la entidad NO tiene ID -> CREAR
        await postEntidad(
          entidadData.descripcion,
          entidadData.apellido,
          entidadData.telefono,
          entidadData.email,
          entidadData.dniCuitCuil,
          entidadData.direccion,
          entidadData.localidad,
          entidadData.provincia,
          idTipoEntidad,
          idCondicionIvaEntidad,
        )
      }

      handleClose(true) // Cierra el modal y actualiza la tabla
    } catch (err) {
      console.error("Error al guardar proveedor:", err)
      setError("Ocurrió un error al guardar los datos. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={() => !loading && handleClose(false)} closeAfterTransition>
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 600, md: 700 },
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* Encabezado */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              pl: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
  <BusinessIcon
    sx={{
      fontSize: 24,
      color: 'rgb(244, 147, 14)',
      mr: 1.5,
    }}
  />
  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
    {`${entidadData.id ? "Editar" : "Crear Nuevo"} ${
      idTipoEntidad === 1
        ? "Cliente"
        : idTipoEntidad === 2
        ? "Proveedor"
        : "Entidad"
    }`}
  </Typography>
</Box>

            <IconButton
              onClick={() => handleClose(false)}
              disabled={loading}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Contenido del formulario */}
          <Box
            sx={{
              p: 3,
              maxHeight: "calc(90vh - 130px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.text.primary, 0.2),
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: alpha(theme.palette.text.primary, 0.05),
              },
            }}
          >
            {/* Mensaje de error */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Sección: Información Personal */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <PersonIcon sx={{ color: 'rgb(244, 147, 14)',mr: 1 }} />
                  Información Personal
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre / Razón Social"
                  name="descripcion"
                  value={entidadData.descripcion || ""}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.descripcion}
                  helperText={errors.descripcion}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Apellido"
                  name="apellido"
                  value={entidadData.apellido || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="DNI / CUIT / CUIL"
                  name="dniCuitCuil"
                  value={entidadData.dniCuitCuil || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                >
                  <InputLabel>Condición IVA</InputLabel>
                  <Select
                    value={idCondicionIvaEntidad}
                    onChange={(e) => setIdCondicionIvaEntidad(e.target.value)}
                    label="Condición IVA"
                  >
                    <MenuItem value="">
                      <em>Sin seleccionar</em>
                    </MenuItem>
                    {condicionIva.map((condicion) => (
                      <MenuItem key={condicion.id} value={condicion.id}>
                        {condicion.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Sección: Información de Contacto */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <PhoneIcon sx={{ mr: 1, color: 'rgb(244, 147, 14)' }} />
                  Información de Contacto
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Teléfono"
                  name="telefono"
                  value={entidadData.telefono || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={entidadData.email || ""}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Sección: Dirección */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <LocationOnIcon  sx={{ mr: 1, color: 'rgb(244, 147, 14)' }} />
                  Dirección
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Dirección"
                  name="direccion"
                  value={entidadData.direccion || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Localidad"
                  name="localidad"
                  value={entidadData.localidad || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Provincia"
                  name="provincia"
                  value={entidadData.provincia || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApartmentIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Footer con botones */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleClose(false)}
              disabled={loading}
              sx={{
                mr: 2,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                minWidth: 120,
                boxShadow: 2,
              }}
            >
              {loading ? "Guardando..." : entidadData.id ? "Actualizar" : "Crear"}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  )
}

export default ModalCrearActualizarEntidad

